<template>
  <form class="reset-password-form" @submit.prevent="formSubmitted">
    <h1 class="text-center">Reset Password</h1>
    <p class="text-center">Please type in your desired new password twice below.</p>
    <lp-input v-if="!username" v-model="formUsername" type="text" label-top="Email"  />
    <lp-input v-model="password" type="password" label-top="New password"  />
    <lp-input v-model="repeatPassword" type="password" label-top="Repeat password"  />
    <PasswordPoliciesList :isSymbolsPolicyMeet="isSymbolsPolicyMeet" :isLengthPolicyMeet="isLengthPolicyMeet"
      :isLowercasePolicyMeet="isLowercasePolicyMeet" :isRequireNumberPolicyMeet="isRequireNumberPolicyMeet"
      :isUppercasePolicyMeet="isUppercasePolicyMeet"></PasswordPoliciesList>
    <div class="ly-row mt-2 ly-even-2">
      <button @click="goBack" type="button"> Go Back</button>
      <lp-button :disabled="disableForm || props.isSubmitting" :loading="props.isSubmitting" type="primary" class="btn-loading-active">Send</lp-button>
    </div>
  </form>
</template>
<script setup lang="ts">
import { ref, defineEmits, computed, Ref, onMounted, defineProps, inject } from 'vue'
import { useRoute } from 'vue-router'
import PasswordPoliciesList from '@/views/Common/components/PasswordPoliciesList.vue'
import { useConfigSync } from '@/config/useConfig'
import { Redirect } from '@/types/redirect'

const redirect = inject('redirect') as Redirect
const config = useConfigSync()
const props = defineProps(['isSubmitting'])
const route = useRoute()
const username: Ref<string> = ref('')
const code: Ref<string> = ref('')
const password: Ref<string> = ref('')
const repeatPassword: Ref<string> = ref('')
const formUsername: Ref<string> = ref('')
const emit = defineEmits(['formSubmitted', 'errorCatched'])
const disableForm = computed(() => {
  return !code.value ||
    !password.value ||
    !repeatPassword.value ||
    password.value !== repeatPassword.value ||
    !(username.value || formUsername.value) ||
    !isSymbolsPolicyMeet.value ||
    !isLengthPolicyMeet.value ||
    !isLowercasePolicyMeet.value ||
    !isRequireNumberPolicyMeet.value ||
    !isUppercasePolicyMeet.value
})

const displayError = (error : string | any) => {
  emit('errorCatched', error)
}

onMounted(() => {
  try {
    if (route?.query?.token) {
      const tokenData = JSON.parse(atob(route?.query?.token))

      if (!tokenData?.code) displayError({ message: 'The token provided is not valid or is already expired' })
      username.value = window.localStorage.getItem('email') || tokenData?.email

      code.value = `${tokenData?.code}`
    }
  } catch (error) {
    displayError({ message: 'The token provided is not valid or is already expired' })
    console.error('The token provided is not valid or is already expired')
  }
})

const formSubmitted = () => {
  emit('formSubmitted', {
    username: username.value || formUsername.value,
    code: code.value,
    password: password.value
  })
}

const isSymbolsPolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_symbols) return true
  return (/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/.test(password.value))
})

const isLengthPolicyMeet = computed((): boolean => {
  return password.value.length >= config?.password_policy.minimum_length
})

const isLowercasePolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_lowercase) return true
  return (/[a-z]/.test(password.value))
})

const isRequireNumberPolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_numbers) return true
  return /\d/.test(password.value)
})

const isUppercasePolicyMeet = computed((): boolean => {
  if (!config.password_policy.require_uppercase) return true
  return (/[A-Z]/.test(password.value))
})

const goBack = () => {
  redirect('LoginView')
}

</script>

<style>
.reset-password-form {
  margin: 0px 0px;
  padding: 0px 0px;
}
</style>
